import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";
import NewsCard from "@/components/feature/NewsCard";

export default function NewsDetail() {
  const { id } = useParams();
  const [toast, setToast] = useState("");
  const article = newsList.find((n) => n.id === id);

  const related = useMemo(() => {
    if (!article) return [];
    return newsList
      .filter((n) => n.id !== article.id)
      .sort((a, b) => {
        const sa = a.category === article.category ? 1 : 0;
        const sb = b.category === article.category ? 1 : 0;
        return sb - sa;
      })
      .slice(0, 3);
  }, [article]);

  usePageMeta({
    title: article ? `${article.title} | 国产精品视频久久` : "影视资讯 | 国产精品久久",
    description: article ? article.summary : "国产精品久久影视资讯详情。",
    keywords: article ? `${article.category},影视资讯,${article.title}` : "影视资讯",
  });

  const share = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制，快去分享给朋友吧");
    } catch {
      setToast(`复制失败，请手动复制：${url}`);
    }
    setTimeout(() => setToast(""), 2500);
  };

  if (!article) {
    return (
      <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-file-search-line text-3xl" />
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">未找到该资讯</h1>
        <p className="mt-2 text-sm text-foreground-500">内容可能已删除，或链接有误</p>
        <Link to="/news" className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
          返回资讯列表
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <Link to="/news" className="transition-colors hover:text-primary-600">影视资讯</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="truncate text-foreground-800">{article.category}</span>
      </nav>

      <article className="mt-4 max-w-4xl">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-900">
            {article.category}
          </span>
          <span className="text-sm text-foreground-500">{article.date}</span>
        </div>
        <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
          {article.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-5 text-sm text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line" />
            {article.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-bookmark-line" />
            来源：{article.source}
          </span>
        </div>

        <div className="mt-6 w-full overflow-hidden rounded-lg bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} - 影视资讯`}
            className="aspect-[16/9] h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-6 flex flex-col gap-5">
          {article.content.map((paragraph, index) => (
            <p key={index} className="text-sm leading-loose text-foreground-700 md:text-base">
              {paragraph}
            </p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
          <button
            type="button"
            onClick={share}
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-share-line" />
            分享这篇资讯
          </button>
          <Link
            to="/news"
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回资讯列表
          </Link>
        </div>
      </article>

      {related.length > 0 && (
        <section className="mt-14">
          <h2 className="mb-6 flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <span className="h-6 w-1.5 rounded-full bg-primary-500" />
            相关文章
          </h2>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <NewsCard key={item.id} article={item} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 rounded-md bg-foreground-950 px-5 py-3 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}